// src/components/agent/AgentPropertiesMap.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChevronLeft, Navigation, Locate } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../../contexts/AuthContext';
import { agentsAPI } from '../../services/api';
import { getDisplayStatus, getDisplayAddress, hideImported, formatValuationNo, getPropertyBillPrimary, getPropertyBillSecondary } from '../../utils/displayName';
import AgentBottomNav from './AgentBottomNav';

const OSRM_BASE = 'https://router.project-osrm.org/route/v1/driving';

function distanceMetres(lat1, lng1, lat2, lng2) {
  const R = 6371e3;
  const rad = (x) => (x * Math.PI) / 180;
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Fix default marker icon in Leaflet with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

/** Zoom map to the loaded driving route so the full path stays in view. */
function FitDrivingRoute({ routePoints, allRoutePoints, agentPos }) {
  const map = useMap();
  useEffect(() => {
    const line = allRoutePoints.length > 0 ? allRoutePoints : routePoints;
    if (!line || line.length < 2) return;
    const pts = [...line];
    if (agentPos && agentPos.length === 2) pts.push(agentPos);
    const bounds = L.latLngBounds(pts);
    map.fitBounds(bounds, { padding: [72, 72], maxZoom: 16, animate: true });
  }, [map, routePoints, allRoutePoints, agentPos]);
  return null;
}

function FitBounds({ positions, agentPos }) {
  const map = useMap();
  useEffect(() => {
    const all = agentPos ? [...positions, agentPos] : positions;
    if (all.length === 0) return;
    if (all.length === 1) {
      map.setView(all[0], 16);
      return;
    }
    const bounds = L.latLngBounds(all);
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
  }, [map, positions, agentPos]);
  return null;
}

const statusColor = (status) => {
  if (status === 'delivered' || status === 'paid') return '#2196F3';
  return '#DC2626';
};

const AgentPropertiesMap = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agentLocation, setAgentLocation] = useState(null);
  const [routePoints, setRoutePoints] = useState([]);
  /** Single-leg “next” route fetch */
  const [routeNextLoading, setRouteNextLoading] = useState(false);
  /** Multi-leg “all” route: null when idle; while loading, done/total legs drawn so far */
  const [routeAllProgress, setRouteAllProgress] = useState(null);

  const fetchAssignedBills = async () => {
    try {
      setLoading(true);
      const data = await agentsAPI.getAssignedBills(user?.id ?? '', '');
      setBills(data || []);
    } catch (error) {
      toast.error('Failed to load properties');
      console.error('Error fetching bills:', error);
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchAssignedBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setAgentLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [user]);

  const withCoords = useMemo(() => bills.filter((p) => {
    const lat = p.gpsLat ?? p.gps_lat;
    const lng = p.gpsLng ?? p.gps_lng;
    return lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng));
  }), [bills]);

  const pendingWithCoords = useMemo(
    () => withCoords.filter((p) => p.status !== 'delivered' && p.status !== 'paid'),
    [withCoords]
  );

  const sortedByDistance = useMemo(() => {
    if (!agentLocation || pendingWithCoords.length === 0) return pendingWithCoords;
    return [...pendingWithCoords].sort((a, b) => {
      const latA = Number(a.gpsLat ?? a.gps_lat);
      const lngA = Number(a.gpsLng ?? a.gps_lng);
      const latB = Number(b.gpsLat ?? b.gps_lat);
      const lngB = Number(b.gpsLng ?? b.gps_lng);
      return (
        distanceMetres(agentLocation.lat, agentLocation.lng, latA, lngA) -
        distanceMetres(agentLocation.lat, agentLocation.lng, latB, lngB)
      );
    });
  }, [agentLocation, pendingWithCoords]);

  /** All properties with coords, nearest-first — used for “Show route to all” (not only pending). */
  const sortedAllByDistance = useMemo(() => {
    if (!agentLocation || withCoords.length === 0) return withCoords;
    return [...withCoords].sort((a, b) => {
      const latA = Number(a.gpsLat ?? a.gps_lat);
      const lngA = Number(a.gpsLng ?? a.gps_lng);
      const latB = Number(b.gpsLat ?? b.gps_lat);
      const lngB = Number(b.gpsLng ?? b.gps_lng);
      return (
        distanceMetres(agentLocation.lat, agentLocation.lng, latA, lngA) -
        distanceMetres(agentLocation.lat, agentLocation.lng, latB, lngB)
      );
    });
  }, [agentLocation, withCoords]);

  const closestNext = sortedByDistance[0] ?? null;
  const [allRoutePoints, setAllRoutePoints] = useState([]);

  const fetchRoute = async (from, to) => {
    if (!from || !to) return;
    setRouteNextLoading(true);
    setRouteAllProgress(null);
    setRoutePoints([]);
    setAllRoutePoints([]);
    try {
      const url = `${OSRM_BASE}/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.code === 'Ok' && data.routes?.[0]?.geometry?.coordinates?.length) {
        const coords = data.routes[0].geometry.coordinates;
        const latLngs = coords.map(([lng, lat]) => [lat, lng]);
        setRoutePoints(latLngs);
      } else {
        toast.error('Could not compute road route. Try Open in Maps.');
      }
    } catch (err) {
      console.error('Route fetch failed:', err);
      toast.error('Could not load route');
    } finally {
      setRouteNextLoading(false);
    }
  };

  /** Chained 2-point OSRM legs; map updates after each leg (no wait for full run). */
  const fetchRouteThroughAll = async (from, stops) => {
    if (!from || !stops || stops.length === 0) return;
    setRouteNextLoading(false);
    setRoutePoints([]);
    setAllRoutePoints([]);
    const maxStops = Math.min(stops.length, 50);
    setRouteAllProgress({ done: 0, total: maxStops });
    try {
      const merged = [];
      let current = { lat: from.lat, lng: from.lng };
      for (let i = 0; i < maxStops; i++) {
        const next = stops[i];
        const url = `${OSRM_BASE}/${current.lng},${current.lat};${next.lng},${next.lat}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.code !== 'Ok' || !data.routes?.[0]?.geometry?.coordinates?.length) {
          toast.error('Could not compute route through all stops.');
          return;
        }
        const latLngs = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        if (merged.length > 0 && latLngs.length > 0) merged.push(...latLngs.slice(1));
        else merged.push(...latLngs);
        current = next;
        setAllRoutePoints([...merged]);
        setRouteAllProgress({ done: i + 1, total: maxStops });
      }
    } catch (err) {
      console.error('Route(all) fetch failed:', err);
      toast.error('Could not load full route');
    } finally {
      setRouteAllProgress(null);
    }
  };

  const showRouteToNext = () => {
    if (!agentLocation || !closestNext) {
      toast.info('Allow location access and ensure you have pending properties.');
      return;
    }
    const lat = Number(closestNext.gpsLat ?? closestNext.gps_lat);
    const lng = Number(closestNext.gpsLng ?? closestNext.gps_lng);
    fetchRoute(agentLocation, { lat, lng, prop: closestNext });
  };

  const openInMaps = (lat, lng) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const positions = useMemo(() => withCoords.map((p) => {
    const lat = p.gpsLat ?? p.gps_lat;
    const lng = p.gpsLng ?? p.gps_lng;
    return [Number(lat), Number(lng)];
  }), [withCoords]);

  const defaultCenter = [7.9465, -1.0232];
  const noCoordsCount = bills.length - withCoords.length;
  const agentPos = agentLocation ? [agentLocation.lat, agentLocation.lng] : null;
  const routeBusy = routeNextLoading || routeAllProgress != null;
  /** Partial polyline on map, more legs still fetching */
  const allRouteStillLoading =
    routeAllProgress != null &&
    routeAllProgress.done > 0 &&
    routeAllProgress.done < routeAllProgress.total;

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <header className="p-6 bg-[#001E3C] flex items-center gap-4 border-b border-white/10">
          <button type="button" onClick={() => navigate('/agent/dashboard')} className="glass-card p-3 rounded-xl">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <h2 className="text-xl font-black uppercase text-white">Properties Map</h2>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <header className="p-6 bg-[#001E3C] flex items-center gap-4 border-b border-white/10">
        <button type="button" onClick={() => navigate('/agent/dashboard')} className="glass-card p-3 rounded-xl">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <h2 className="text-xl font-black uppercase text-white">Properties Map</h2>
      </header>

      <div className="flex-1 relative min-h-[300px] bg-[#001021]">
        <MapContainer
          center={positions.length ? positions[0] : defaultCenter}
          zoom={positions.length ? 12 : 7}
          className="w-full h-full z-0"
          style={{ minHeight: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds positions={positions} agentPos={agentPos} />
          <FitDrivingRoute routePoints={routePoints} allRoutePoints={allRoutePoints} agentPos={agentPos} />
          {routePoints.length > 0 && (
            <Polyline positions={routePoints} pathOptions={{ color: '#00E676', weight: 5, opacity: 0.9 }} />
          )}
          {allRoutePoints.length > 0 && (
            <Polyline
              positions={allRoutePoints}
              pathOptions={{
                color: '#FFC107',
                weight: 5,
                opacity: allRouteStillLoading ? 0.75 : 0.95,
              }}
            />
          )}
          {agentPos && (
            <Marker
              position={agentPos}
              icon={L.divIcon({
                className: 'agent-marker',
                html: '<div style="width:24px;height:24px;border-radius:50%;background:#00F5FF;border:3px solid #001021;box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>',
                iconSize: [24, 24],
                iconAnchor: [12, 12],
              })}
            >
              <Popup>You are here</Popup>
            </Marker>
          )}
          {withCoords.map((prop) => {
            const lat = prop.gpsLat ?? prop.gps_lat;
            const lng = prop.gpsLng ?? prop.gps_lng;
            const pos = [Number(lat), Number(lng)];
            const color = statusColor(prop.status);
            const houseSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="${color}" stroke="#001021" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`;
            const icon = L.divIcon({
              className: 'house-marker',
              html: `<div style="display:flex;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">${houseSvg}</div>`,
              iconSize: [28, 28],
              iconAnchor: [14, 28],
              popupAnchor: [0, -28],
            });
            return (
              <Marker key={prop.valuationNo || prop.billNo || prop.id} position={pos} icon={icon}>
                <Popup>
                  <div className="text-sm min-w-[200px]">
                    <div className="font-bold text-base border-b border-gray-300 pb-1 mb-2">House details</div>
                    <p><strong>House ID:</strong> {getPropertyBillPrimary(prop)}</p>
                    {getPropertyBillSecondary(prop) && (
                      <p><strong>Valuation:</strong> {getPropertyBillSecondary(prop)}</p>
                    )}
                    <p><strong>Owner:</strong> {hideImported(prop.ownerName ?? prop.owner_name, 'Not provided')}</p>
                    <p><strong>Phone:</strong> {hideImported(prop.ownerPhone ?? prop.owner_phone, 'Not provided')}</p>
                    <p><strong>Address:</strong> {getDisplayAddress(prop)}</p>
                    <p><strong>Status:</strong> {getDisplayStatus(prop.status)}</p>
                    <p><strong>Amount payable:</strong> ₵{(Number(prop.totalDue ?? prop.total_due) || 0).toLocaleString()}</p>
                    <button
                      type="button"
                      onClick={() => navigate(`/agent/property/${encodeURIComponent(prop.id || formatValuationNo(prop.valuationNo) || prop.billNo)}`)}
                      className="mt-2 inline-block px-3 py-1 bg-[#00F5FF] text-[#001021] rounded font-bold text-xs"
                    >
                      View bill
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        <div className="absolute bottom-4 left-2 right-2 bg-gray-800/50 backdrop-blur-sm p-2 z-[1000] space-y-3">
          {agentLocation && withCoords.length > 0 && (
            <div className="flex flex-col gap-2">
              {closestNext && (
                <p className="text-xs text-white/80">
                  Next: {getPropertyBillPrimary(closestNext)} — {getDisplayAddress(closestNext, 'Property')}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {closestNext && (
                  <button
                    type="button"
                    onClick={showRouteToNext}
                    disabled={routeBusy}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#00E676] text-[#001021] text-xs font-bold disabled:opacity-60"
                  >
                    <Navigation className="w-4 h-4" />
                    {routeNextLoading ? 'Loading route…' : 'Show route to next'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (!agentLocation) return toast.info('Allow location access to build the route.');
                    const stops = sortedAllByDistance
                      .slice(0, 50)
                      .map((p) => ({
                        lat: Number(p.gpsLat ?? p.gps_lat),
                        lng: Number(p.gpsLng ?? p.gps_lng),
                      }))
                      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
                    fetchRouteThroughAll(agentLocation, stops);
                  }}
                  disabled={routeBusy}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FFC107] text-[#001021] text-xs font-bold disabled:opacity-60"
                >
                  <Navigation className="w-4 h-4" />
                  {routeAllProgress
                    ? routeAllProgress.done === 0
                      ? 'Loading route…'
                      : `Loading route… ${routeAllProgress.done}/${routeAllProgress.total}`
                    : 'Show route to all'}
                </button>
                {closestNext && (
                  <button
                    type="button"
                    onClick={() => openInMaps(Number(closestNext.gpsLat ?? closestNext.gps_lat), Number(closestNext.gpsLng ?? closestNext.gps_lng))}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 text-white text-xs font-bold"
                  >
                    <Locate className="w-4 h-4" />
                    Open in Maps
                  </button>
                )}
              </div>
              {allRouteStillLoading && (
                <p className="text-[10px] text-white/70">
                  Showing route on map as each leg loads ({routeAllProgress.done}/{routeAllProgress.total}).
                </p>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#DC2626]" />
                <span className="text-xs text-white">Undelivered</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#2196F3]" />
                <span className="text-xs text-white">Delivered</span>
              </div>
              {agentLocation && (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#00F5FF]" />
                  <span className="text-xs text-white">You</span>
                </div>
              )}
            </div>
            <span className="text-xs text-white/80">
              {withCoords.length} houses on map
              {noCoordsCount > 0 && ` · ${noCoordsCount} missing lat/lng`}
            </span>
          </div>
        </div>
      </div>

      <AgentBottomNav active={null} />
    </div>
  );
};

export default AgentPropertiesMap;
